/**
 * NuWay color system — V10 "Signal Yellow" (docs/design-system/COLORS.md).
 *
 * Warm neutrals carry the UI; Signal Yellow marks the one action or the
 * current place. Black on yellow, always (white on #F5C400 is 1.64:1). No
 * violet, no blue. Components use the semantic tokens below (as `--nv-*` CSS
 * variables or the MUI palette), never the primitive ramps directly.
 */

export const ramps = {
  yellow: {
    0: '#FFFDF5',
    50: '#FFF9E1',
    100: '#FFF4CC',
    200: '#FCE9AA',
    300: '#F9DD85',
    400: '#F7D15A',
    500: '#F5C400',
    600: '#E0B000',
    700: '#A47F00',
    800: '#6B5200',
    900: '#3D2F00'
  },
  neutral: {
    0: '#FFFFFF',
    50: '#FAF9F6',
    100: '#F1EEE9',
    200: '#E4DFD7',
    300: '#D3CEC6',
    400: '#B8AEA4',
    500: '#8C8178',
    600: '#5A5048',
    700: '#3A332D',
    750: '#272220',
    800: '#1F1B18',
    850: '#1A1714',
    900: '#141210'
  },
  espresso: {
    0: '#FBF8F6',
    50: '#EDE9E7',
    100: '#DFDAD8',
    200: '#C4BEBA',
    300: '#A9A29E',
    400: '#8F8782',
    500: '#766C67',
    600: '#5D534E',
    700: '#463B36',
    800: '#30251F',
    900: '#1C1512'
  },
  green: {
    0: '#F6FAF7',
    50: '#EFF5F0',
    100: '#E8F1EA',
    200: '#B5DEBE',
    300: '#7FCB92',
    400: '#6CB27D',
    500: '#599A68',
    600: '#468254',
    700: '#386343',
    800: '#2B4632',
    900: '#1E2B21'
  },
  red: {
    0: '#FFF8F6',
    50: '#FEEEEC',
    100: '#FCE5E1',
    200: '#F4C4BC',
    300: '#EAA398',
    400: '#DF8274',
    500: '#D15F51',
    600: '#C2372B',
    700: '#922B23',
    800: '#64201B',
    900: '#3A1411'
  },
  orange: {
    0: '#FFF9F3',
    50: '#FEF2E5',
    100: '#FDEBD6',
    200: '#F1D1B0',
    300: '#E5B68A',
    400: '#DA9B65',
    500: '#CE7E40',
    600: '#C2610C',
    700: '#914B08',
    800: '#643608',
    900: '#3A2108'
  }
} as const;

const { yellow, neutral, espresso, green, red, orange } = ramps;

export type SemanticTokens = Record<string, string>;

/** Semantic tokens (COLORS.md §3). Keys are the CSS variable names without `--nv-`. */
export const lightTokens: SemanticTokens = {
  'bg-page': neutral[50],
  'bg-surface': neutral[0],
  'bg-chrome': neutral[0],
  'bg-subtle': neutral[100],
  'bg-hover': neutral[50],
  'bg-inverse': '#000000',
  'bg-feature': espresso[800],
  'bg-customer-bubble': '#F1F2F4',
  'text-primary': '#000000',
  'text-secondary': '#313130',
  'text-tertiary': neutral[600],
  'text-disabled': neutral[400],
  'text-on-brand': '#000000',
  'text-on-inverse': '#FFFFFF',
  'text-on-feature': neutral[50],
  'border-default': neutral[300],
  'border-subtle': neutral[200],
  'border-input': neutral[500],
  'border-strong': '#000000',
  'focus-ring': '#000000',
  brand: yellow[500],
  'brand-hover': yellow[600],
  'brand-pressed': yellow[700],
  'brand-soft': yellow[100],
  'brand-on-soft': yellow[800],
  success: green[600],
  'success-soft': green[100],
  'success-text': green[700],
  warning: orange[600],
  'warning-soft': orange[100],
  'warning-text': orange[700],
  danger: red[600],
  'danger-soft': red[100],
  'danger-text': red[700],
  'info-soft': neutral[100],
  'info-text': '#313130',
  'chart-1': yellow[500],
  'chart-2': espresso[800],
  'chart-3': green[500],
  'chart-4': orange[500],
  'chart-5': neutral[400],
  'chart-grid': neutral[200],
  scrim: 'rgba(20,18,16,.48)',
  'shadow-float': '0 8px 24px rgba(20,18,16,.12)'
};

export const darkTokens: SemanticTokens = {
  'bg-page': neutral[900],
  'bg-surface': neutral[800],
  'bg-chrome': neutral[850],
  'bg-subtle': neutral[750],
  'bg-hover': neutral[750],
  'bg-inverse': neutral[50],
  'bg-feature': espresso[800],
  'bg-customer-bubble': neutral[700],
  'text-primary': neutral[50],
  'text-secondary': neutral[400],
  'text-tertiary': neutral[500],
  'text-disabled': neutral[600],
  'text-on-brand': '#000000',
  'text-on-inverse': '#000000',
  'text-on-feature': neutral[50],
  'border-default': neutral[700],
  'border-subtle': neutral[750],
  'border-input': neutral[500],
  'border-strong': neutral[600],
  'focus-ring': yellow[500],
  brand: yellow[500],
  'brand-hover': yellow[400],
  'brand-pressed': yellow[600],
  'brand-soft': 'rgba(245,196,0,.14)',
  'brand-on-soft': yellow[300],
  success: green[300],
  'success-soft': green[900],
  'success-text': green[300],
  warning: orange[400],
  'warning-soft': orange[900],
  'warning-text': orange[300],
  danger: red[500],
  'danger-soft': red[900],
  'danger-text': red[300],
  'info-soft': neutral[750],
  'info-text': neutral[300],
  'chart-1': yellow[500],
  'chart-2': espresso[300],
  'chart-3': green[400],
  'chart-4': orange[400],
  'chart-5': neutral[500],
  'chart-grid': neutral[750],
  scrim: 'rgba(0,0,0,.64)',
  'shadow-float': '0 8px 24px rgba(0,0,0,.48)'
};

/**
 * The app's older `--nv-*` names (≈2,000 uses) mapped onto V10 roles, so every
 * existing screen picks up the new palette without touching each component.
 * New code should prefer the semantic names above.
 */
const legacyAliases = (t: SemanticTokens, dark: boolean): SemanticTokens => ({
  bg: t['bg-page'],
  surface: t['bg-surface'],
  'surface-soft': t['bg-subtle'],
  'surface-softer': dark ? t['bg-chrome'] : t['bg-page'],
  'surface-hover': t['bg-hover'],
  'surface-raised': t['bg-surface'],
  'text-strong': t['text-primary'],
  'text-muted': t['text-tertiary'],
  'text-subtle': dark ? neutral[600] : neutral[500],
  border: t['border-subtle'],
  // inputs and card outlines used --nv-border-strong; V10 cards use border-default
  'border-strong': t['border-default'],
  divider: t['border-subtle'],
  primary: t.brand,
  'primary-hover': t['brand-hover'],
  'primary-soft': t['brand-soft'],
  'primary-softer': t['bg-subtle'],
  'success-softer': t['success-soft'],
  // violet is retired: its soft tint becomes the neutral one
  'purple-soft': t['bg-subtle'],
  'purple-text': t['text-secondary'],
  accent: t.brand,
  'gold-soft': t['brand-soft'],
  'gold-border': dark ? 'rgba(245,196,0,.55)' : 'rgba(164,127,0,.45)',
  'gold-text': t['brand-on-soft'],
  'shadow-color': dark ? 'rgba(0,0,0,.48)' : 'rgba(20,18,16,.12)'
});

/** All CSS variables for one mode, ready for GlobalStyles `:root`. */
export const cssVariables = (dark: boolean): Record<string, string> => {
  const t = dark ? darkTokens : lightTokens;
  // V10's own `border-strong` (#000, outline buttons) clashes with the legacy name;
  // expose it as --nv-border-outline and keep --nv-border-strong for the legacy role.
  const vars: Record<string, string> = {};
  Object.entries(t).forEach(([k, v]) => {
    vars[`--nv-${k === 'border-strong' ? 'border-outline' : k}`] = v;
  });
  Object.entries(legacyAliases(t, dark)).forEach(([k, v]) => {
    vars[`--nv-${k}`] = v;
  });
  return vars;
};
